import { bool, func, object } from 'prop-types';
import React, { Component, createRef } from 'react';
import styled, { withTheme } from 'styled-components';

import { DURATION } from 'ravenjs/constants/transitions';
import Menu from 'ravenjs/lib/Menu';
import PopoverContent from 'ravenjs/lib/PopoverContent';
import { getThemeProps } from 'ravenjs/utils/theme';
import { get } from 'ravenjs/utils/lodash';

import { ProfileIcon } from 'components/Icons';
import UserMenu from 'components/UserMenu';
import withPublicContent from 'components/PublicContent/PublicContentHOC';
import { DEFAULT_COLORS } from 'constants/colors';
import { APP_PERMISSIONS } from 'constants/permissions';
import Acl from 'modules/acl';
import { hasFeatureFlag } from 'utils/common';
import {
    DESKTOP_BREAKPOINT,
    DROPDOWN_OPEN_EVENT,
    HOVER_INTENT_DELAY,
    SUBMENU_CLOSE_DELAY,
} from 'constants/navigation';

import UserNavStyled from './UserNavStyled';

const PopoverStylesV1 = styled(PopoverContent)`
    bottom: 0;
    margin-left: 8px;
    &:before {
        background: ${DEFAULT_COLORS.WHITE};
        box-shadow: none;
        border-top: 1px solid ${DEFAULT_COLORS.BLACK};
        border-right: 1px solid ${DEFAULT_COLORS.BLACK};
    }
    max-width: 745px;
`;

const PopoverStylesV2 = styled(PopoverContent)`
    bottom: 0;
    margin-left: 8px;
    margin-top: 30px;
    &:before {
        display: none;
    }
    width: fit-content;
    max-width: calc(100vw - 5.125rem);
`;

const IconWrapper = styled.div`
    align-items: center;
    display: flex;
    position: relative;
    cursor: pointer;
    height: 100%;

    ${({ theme }) => theme.media.up('md')`
        margin: 0 .5625rem;
    `};
    ${({ theme }) => theme.media.down('md')`
        margin: 0;
    `};
`;

class UserNav extends Component {
    static propTypes = {
        history: object,
        items: object,
        user: object,
        theme: object.isRequired,
        isSoftLogin: bool.isRequired,
        onNavClick: func,
        openLoginPrompt: func.isRequired,
    };

    static defaultProps = {
        history: {},
        items: {},
        onNavClick: () => {},
        user: {},
    };

    userNavRef = createRef();

    containerRef = createRef();

    constructor(props) {
        super(props);
        this.state = {
            hover: false,
            isDesktop: this.checkIsDesktop(),
        };

        this.hoverIntentTimeout = null;
        this.closeDelayTimeout = null;
    }

    componentDidMount() {
        globalThis.addEventListener('resize', this.handleResize);
        document.addEventListener('mousedown', this.handleClickOutside);
        globalThis.addEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
    }

    componentWillUnmount() {
        globalThis.removeEventListener('resize', this.handleResize);
        document.removeEventListener('mousedown', this.handleClickOutside);
        globalThis.removeEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
        this.clearAllTimeouts();
    }

    isNavigationV2Enabled = () => {
        const { history } = this.props;
        return history?.location ? hasFeatureFlag(history.location, 'showNavigationV2') : false;
    };

    clearAllTimeouts = () => {
        if (this.hoverIntentTimeout) {
            clearTimeout(this.hoverIntentTimeout);
            this.hoverIntentTimeout = null;
        }
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    checkIsDesktop = () => {
        return typeof globalThis !== 'undefined' && globalThis.innerWidth >= DESKTOP_BREAKPOINT;
    };

    handleResize = () => {
        const isDesktop = this.checkIsDesktop();
        if (isDesktop !== this.state.isDesktop) {
            this.setState(prevState => {
                const nextState = { isDesktop };
                // Close the menu when switching between desktop and mobile (V2 only)
                if (!isDesktop && this.isNavigationV2Enabled()) {
                    this.clearAllTimeouts();
                    if (prevState.hover) {
                        nextState.hover = false;
                    }
                }
                return nextState;
            });
        }
    };

    handleClickOutside = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }

        // Check if the click is within the container (UserNavStyled)
        const isInContainer =
            this.containerRef.current && this.containerRef.current.contains(event.target);

        // Check if the click is within the menu content (PopoverContent); The Menu component stores a ref to its content in menuContentRef
        const menuContentRef = this.userNavRef.current?.menuContentRef?.current;
        const isInMenuContent = menuContentRef && menuContentRef.contains(event.target);

        // Only close if the click is outside both the container and menu content
        if (!isInContainer && !isInMenuContent) {
            this.clearAllTimeouts();
            this.setState({ hover: false });
        }
    };

    handleOtherDropdownOpen = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }
        // If another dropdown opened (not this one), close immediately
        if (event.detail && event.detail.source !== 'avatar') {
            this.clearAllTimeouts();
            this.setState({ hover: false });
        }
    };

    notifyDropdownOpen = () => {
        globalThis.dispatchEvent(
            new CustomEvent(DROPDOWN_OPEN_EVENT, {
                detail: { source: 'avatar' },
            })
        );
    };

    closeMenu = navLink => {
        const { onNavClick } = this.props;
        const { current } = this.userNavRef;

        this.clearAllTimeouts();

        current && current.closeMenu();

        this.setState({ hover: false });

        onNavClick(navLink);
    };

    renderMenuContent = () => {
        const { history, user } = this.props;
        let { items } = this.props;

        // TODO:  remove once BE change has been made to no longer provide this menu optoin
        const internalTools = get(items, 'internalTools', []).filter(
            item => item.state !== 'ticketingsystem'
        );
        items = { ...items, internalTools };

        return (
            <UserMenu
                history={history}
                user={user}
                items={items}
                onNavClick={navLink => {
                    this.closeMenu(navLink);
                }}
            />
        );
    };

    onAvatarHover = () => {
        const { isSoftLogin } = this.props;
        if (isSoftLogin) {
            return;
        }

        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with hover-intent and persistent hover
        if (isNavV2Enabled) {
            // Clear any pending close timeout when entering
            if (this.closeDelayTimeout) {
                clearTimeout(this.closeDelayTimeout);
                this.closeDelayTimeout = null;
            }

            // For desktop V2, use hover-intent delay
            if (isDesktop) {
                if (this.hoverIntentTimeout) {
                    clearTimeout(this.hoverIntentTimeout);
                }

                this.hoverIntentTimeout = setTimeout(() => {
                    // Notify other dropdowns to close when this one opens
                    this.notifyDropdownOpen();
                    this.setState({ hover: true });
                }, HOVER_INTENT_DELAY);
            } else {
                this.notifyDropdownOpen();
                this.setState({ hover: true });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            this.setState({ hover: true });
        }
    };

    onAvatarClick = () => {
        const { isSoftLogin, openLoginPrompt } = this.props;
        if (isSoftLogin) {
            openLoginPrompt();
        } else {
            // Clear timeouts and open immediately on click
            this.clearAllTimeouts();
            // Notify other dropdowns to close
            if (this.isNavigationV2Enabled()) {
                this.notifyDropdownOpen();
            }
            this.setState({
                hover: true,
            });
        }
    };

    onAvatarLeave = () => {
        const { isSoftLogin } = this.props;
        if (isSoftLogin) {
            return;
        }

        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with delay tolerance for persistent hover
        if (isNavV2Enabled) {
            // Clear hover intent if the user moves away before intent is confirmed
            if (this.hoverIntentTimeout) {
                clearTimeout(this.hoverIntentTimeout);
                this.hoverIntentTimeout = null;
            }

            // For desktop V2, use delay tolerance before closing
            if (isDesktop) {
                if (this.closeDelayTimeout) {
                    clearTimeout(this.closeDelayTimeout);
                }

                this.closeDelayTimeout = setTimeout(() => {
                    this.setState({ hover: false });
                }, SUBMENU_CLOSE_DELAY);
            } else {
                this.setState({ hover: false });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            this.setState({ hover: false });
        }
    };

    // Cancel close when re-entering the menu area (V2 only)
    cancelClose = () => {
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    renderButtonComponent = () => {
        const { theme } = this.props;
        return (
            <IconWrapper>
                <ProfileIcon
                    fill={getThemeProps('NavItem.styles.color', DEFAULT_COLORS.BLACK, { theme })}
                />
            </IconWrapper>
        );
    };

    render() {
        const { history } = this.props;
        const { hover } = this.state;

        const isNavigationV2Enabled = history?.location
            ? hasFeatureFlag(history.location, 'showNavigationV2')
            : false;

        const PopoverStyles = isNavigationV2Enabled ? PopoverStylesV2 : PopoverStylesV1;

        return (
            <UserNavStyled
                ref={this.containerRef}
                className={
                    Acl.check(APP_PERMISSIONS.userAssistanceClientAdmin)
                        ? 'pendo-nav-userprofile'
                        : ''
                }
                onFocus={this.onAvatarHover}
                onMouseEnter={this.onAvatarHover}
                onClick={this.onAvatarClick}
                onMouseLeave={this.onAvatarLeave}
                onBlur={this.onAvatarLeave}
            >
                <Menu
                    ButtonComponent={this.renderButtonComponent()}
                    ContentComponent={PopoverStyles}
                    ContentProps={{
                        borderRadius: isNavigationV2Enabled ? '4px 4px 8px 8px' : '0',
                        elevation: isNavigationV2Enabled ? 0 : 9,
                        // Add mouse handlers for V2 to cancel close when hovering menu content
                        onMouseEnter: isNavigationV2Enabled ? this.cancelClose : undefined,
                        onMouseLeave: isNavigationV2Enabled ? this.onAvatarLeave : undefined,
                    }}
                    IconComponent={null}
                    arrowSize={isNavigationV2Enabled ? 0 : 7}
                    color="transparent"
                    distanceFromContainer={0}
                    placement="bottom-end"
                    ref={this.userNavRef}
                    open={hover}
                    timeout={DURATION.none}
                    borderRadius={4}
                >
                    {this.renderMenuContent()}
                </Menu>
            </UserNavStyled>
        );
    }
}

export { UserNav as UserNavUnwrapped };
export default withPublicContent(withTheme(UserNav));
